import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, Button, TextField, Select, MenuItem,
  InputLabel, FormControl, Typography, Box
} from '@mui/material';
import HelpIcon from './HelpIcon';
import { toYMD } from './DateUtils';
import { logActivity } from '../utils/logActivity';
import { useAuth } from '../context/AuthContext';

const PLANETS = [
  'Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu'
];

const COLORS = [
  'Red', 'Green', 'Blue', 'Yellow', 'Orange', 'Purple', 'Black', 'White', 'Gray', 'Brown', 'Pink', 'Cyan'
];

// Module scope, not inside the component: it is a constant, and rebuilding it every render made
// it a new object each time, which is why the effect that reads it could not list it as a
// dependency without re-running on every render.
const PLANET_DEFAULT_COLORS = {
  Sun: 'Cyan',
  Moon: 'Gray',
  Mercury: 'Green',
  Venus: 'Pink',
  Mars: 'Red',
  Jupiter: 'Orange',
  Saturn: 'Brown',
  Uranus: 'Cyan',
  Neptune: 'Blue',
  Pluto: 'Purple',
  Rahu: 'Black',
  Ketu: 'Teal',
};

const PlanetFollowStudyDialog = ({ open, onClose, onRun, barData }) => {
  const { user, api } = useAuth();
  const [planet1, setPlanet1] = useState('Sun');
  const [planet2, setPlanet2] = useState('Mars');
  const [distance, setDistance] = useState(120);
  const [color, setColor] = useState(COLORS[0]);

  const arePlanetsSame = planet1 === planet2;

  // Auto-update color when planet2 changes
  useEffect(() => {
    setColor(PLANET_DEFAULT_COLORS[planet2] || COLORS[0]);
  }, [planet2]);

  // Auto-update distance when specific planet pairs are selected
  useEffect(() => {
    const isSunMercury = (planet1 === 'Sun' && planet2 === 'Mercury') || (planet1 === 'Mercury' && planet2 === 'Sun');
    const isSunVenus = (planet1 === 'Sun' && planet2 === 'Venus') || (planet1 === 'Venus' && planet2 === 'Sun');

    if (isSunMercury) {
      if (distance > 28) setDistance(28);
    } else if (isSunVenus) {
      if (distance > 48) setDistance(48);
    }
  }, [planet1, planet2, distance]);

  const handleRun = useCallback(async () => {
    const eventDetails = {
      studyType: 'planetFollow',
      parameters: {
        planet1,
        planet2,
        degrees: Number(distance),
      }
    };
    await logActivity(api, user, 'RUN_STUDY', eventDetails);
    try {
      // Always use first and last date in barData.dates
      console.log('[PlanetFollowStudyDialog] barData:', barData);
      const datesArr = barData?.dates || [];
      if (!datesArr.length) {
        alert('No chart data available!');
        return;
      }
      const fromDate = toYMD(datesArr[0]);
      const toDate = toYMD(datesArr[datesArr.length - 1]);
      console.log('[PlanetFollowStudyDialog] Using fromDate:', fromDate, 'toDate:', toDate);

      // Defensive: ensure degrees is a valid number
      const degrees = Number(distance);
      if (isNaN(degrees) || degrees < 0 || degrees > 180) {
        alert('Degrees must be a number between 0 and 180.');
        return;
      }
      const params = new URLSearchParams({
        planet1: planet1,
        planet2: planet2,
        degrees: degrees,
        fromDate,
        toDate
      });
      console.log('[PlanetFollowStudyDialog] Calling getDatesForFollows with:', {
        planet1, planet2, degrees, fromDate, toDate
      });
      const response = await api.get(`/api/followstudy/dates?${params.toString()}`);
      
      const dates = response.data;
      
      if (onRun) {
        onRun({
          dates,
          color,
          planet1,
          planet2,
          degrees, // Use 'degrees' for consistency everywhere
          type: 'planetFollow'
        });
      }
      onClose(); 
    } catch (error) {
      console.error('Error in handleRun:', error);
      alert(`An unexpected error occurred: ${error.message}. Check console for details.`);
    }
    // api belongs here: it is the axios instance carrying the current auth token, memoised in
    // AuthContext on [token, logout], so this recreates only when the token actually changes —
    // which is precisely when the callback should stop using the old one.
  }, [planet1, planet2, distance, color, onRun, onClose, barData, user, api]);

  const handleDistanceChange = (event) => {
    let value = parseInt(event.target.value, 10);
    const isSunMercury = (planet1 === 'Sun' && planet2 === 'Mercury') || (planet1 === 'Mercury' && planet2 === 'Sun');
    const isSunVenus = (planet1 === 'Sun' && planet2 === 'Venus') || (planet1 === 'Venus' && planet2 === 'Sun');
    let maxDistance = 180;
    if (isSunMercury) {
      maxDistance = 28;
    } else if (isSunVenus) {
      maxDistance = 48;
    }

    if (isNaN(value)) {
      value = 0;
    } else if (value < 0) {
      value = 0;
    } else if (value > maxDistance) {
      value = maxDistance;
    }
    setDistance(value);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={false} PaperProps={{ sx: { width: 750, height: 218, minWidth: 750, minHeight: 218, maxWidth: 750, maxHeight: 218, p: 0 } }}>
      <DialogTitle sx={{ fontWeight: 'bold', fontSize: '20px', textAlign: 'center', mb: '24px', mt: '14px', p: 0, lineHeight: 1.2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <span style={{flexGrow: 1, textAlign: 'center', paddingLeft: '48px'}}>Planet-A Follows Planet-B Study</span>
        <HelpIcon helpKey="planetFollowStudyDialog" />
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 0, px: 0, m: 0, height: 120, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', width: '100%', px: '18px', mb: '6px', overflow: 'hidden' }}>
          {/* Left column: controls and color swatches */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mb: '12px' }}>
              <FormControl size="small" sx={{ minWidth: '120px', maxWidth: '120px', height: '38px', mr: '10px' }}>
                <InputLabel id="planet1-select-label">Planet A</InputLabel>
                <Select
                  labelId="planet1-select-label"
                  value={planet1}
                  label="Planet A"
                  onChange={(e) => setPlanet1(e.target.value)}
                  sx={{ height: '38px', fontSize: '16px' }}
                >
                  {PLANETS.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography sx={{ fontWeight: 500, fontSize: '16px', mx: '6px', minWidth: '54px', textAlign: 'center' }}>Follows</Typography>
              <FormControl size="small" sx={{ minWidth: '120px', maxWidth: '120px', height: '38px', mr: '10px' }}>
                <InputLabel id="planet2-select-label">Planet B</InputLabel>
                <Select
                  labelId="planet2-select-label"
                  value={planet2}
                  label="Planet B"
                  onChange={(e) => setPlanet2(e.target.value)}
                  sx={{ height: '38px', fontSize: '16px' }}
                >
                  {PLANETS.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography sx={{ fontWeight: 500, fontSize: '16px', mx: '6px', minWidth: '86px', textAlign: 'center', whiteSpace: 'nowrap' }}>By Degrees</Typography>
              <TextField
                margin="dense"
                label="Distance"
                type="number"
                value={distance}
                onChange={handleDistanceChange}
                inputProps={{
                  min: 0,
                  max: ((planet1 === 'Sun' && planet2 === 'Mercury') || (planet1 === 'Mercury' && planet2 === 'Sun')) ? 28 :
                       ((planet1 === 'Sun' && planet2 === 'Venus') || (planet1 === 'Venus' && planet2 === 'Sun')) ? 48 : 180,
                  step: '1',
                  style: { textAlign: 'center', padding: 0, fontSize: '16px' }
                }}
                sx={{ maxWidth: '75px', width: '75px', height: '38px', '& .MuiInputBase-root': { height: '38px', fontSize: '16px' }, '& .MuiInputLabel-root': { fontSize: '15px', top: '-4px' }, mr: '18px' }}
                size="small"
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: '0px' }}>
              <Typography variant="subtitle1" sx={{ whiteSpace: 'nowrap', minWidth: '108px', fontSize: '16px', pr: '8px' }}>Select color:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {COLORS.map((c) => (
                  <Box
                    key={c}
                    onClick={() => setColor(c)}
                    sx={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      backgroundColor: c.toLowerCase(),
                      border: color === c ? '2.3px solid #1976d2' : '1px solid #bbb',
                      cursor: 'pointer',
                      display: 'inline-block',
                    }}
                    title={c}
                  />
                ))}
              </Box>
            </Box>
          </Box>
          {/* Right column: RUN and CLOSE buttons */}
          <Box sx={{ minWidth: '110px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', ml: 3, mt: 1, mr: '32px' }}>
            <Button
              onClick={handleRun}
              variant="contained"
              color="primary"
              disabled={arePlanetsSame}
              sx={{ width: '90px', height: '38px', fontWeight: 500, fontSize: '16px', borderRadius: '4px', boxShadow: 1, mb: '7px' }}
            >
              RUN
            </Button>
            <Button onClick={onClose} sx={{ color: '#1976d2', fontWeight: 500, fontSize: '16px', minWidth: '60px', p: 0, alignSelf: 'flex-end' }} variant="text">CLOSE</Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

PlanetFollowStudyDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
  barData: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string),
    ohlc: PropTypes.array,
    volumes: PropTypes.array
  })
};

export default PlanetFollowStudyDialog;
